import {ReactElement} from "react";
import {SwissStanding, TournamentTeam} from "@/competitions/tournaments/types";

interface SwissStandingsTableProps {
    standings: SwissStanding[];
    /** Full team data, keyed by id */
    teams?: Record<string, TournamentTeam>;
    /** Legacy support - team ID to name mapping */
    teamNames?: Record<string, string>;
    /** The viewer's own team id — cyan YOU row per the mockups. */
    highlightTeamId?: string;
}

/**
 * Swiss standings table — the mockups' `.standings` treatment (rank, logo+name
 * team cell with cyan YOU row, mono record) over the engine's Swiss
 * tiebreakers (Buchholz, Sonneborn-Berger), rendered in the engine's
 * pre-sorted order (match points → Buchholz → Buchholz-cut → Sonneborn-Berger).
 */
export default function SwissStandingsTable({
    standings,
    teams,
    teamNames,
    highlightTeamId,
}: SwissStandingsTableProps): ReactElement {
    const nameFor = (s: SwissStanding): string =>
        teams?.[s.teamId]?.name ?? teamNames?.[s.teamId] ?? s.name ?? "Unknown";

    return (
        <table className="bracket-viewer__standings">
            <thead>
                <tr>
                    <th className="bracket-viewer__standings-rank">#</th>
                    <th className="bracket-viewer__standings-team">Team</th>
                    <th>Record</th>
                    <th>Pts</th>
                    <th title="Buchholz (sum of opponents' match points)">Buchholz</th>
                    <th title="Sonneborn-Berger (sum of beaten opponents' match points)">SB</th>
                </tr>
            </thead>
            <tbody>
                {standings.map((s, i) => {
                    const isYou = highlightTeamId && s.teamId === highlightTeamId;
                    const logoUrl = teams?.[s.teamId]?.logoUrl;
                    return (
                        <tr key={s.teamId} className={isYou ? "bracket-viewer__standings-row--you" : undefined}>
                            <td className="bracket-viewer__standings-rank">{i + 1}</td>
                            <td className="bracket-viewer__standings-team">
                                <span className="bracket-viewer__standings-cell">
                                    {logoUrl && <img className="bracket-viewer__standings-logo" src={logoUrl} alt="" />}
                                    <span className="bracket-viewer__standings-name">{nameFor(s)}</span>
                                    {isYou && <span className="bracket-viewer__standings-you">YOU</span>}
                                </span>
                            </td>
                            <td className="bracket-viewer__standings-wl">{s.wins}–{s.losses}</td>
                            <td className="bracket-viewer__standings-pts">{s.matchPoints}</td>
                            <td>{s.buchholz}</td>
                            <td>{s.sonnebornBerger}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}
